import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { currencyLabels } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import useSetTourBudget from '@/features/dashboard/tourBudget/hooks/useSetTourBudget.ts';
import { TourBudgetViewDto } from '@/features/dashboard/tourBudget/types/TourBudgetDto.ts';
import { isBudgetDeclared } from '@/features/dashboard/tourBudget/types/TourBudgetDto.utils.ts';
import {
  tourBudgetSchema,
  TourBudgetFormData,
} from '@/features/dashboard/tourBudget/schemas/tourBudgetSchema.ts';
import { getNegativeAmountField } from '@/features/dashboard/tourBudget/api/tourBudgetErrors.ts';

interface SetTourBudgetModalProps {
  tourId: string;
  /**
   * Waluta wybrana wcześniej — kartą waluty. Wtedy pole waluty jest zablokowane,
   * bo budżet w innej walucie to inny rekord, a nie zmiana tego samego.
   */
  budget: TourBudgetViewDto | null;
  /** Waluty bez zadeklarowanego budżetu; używane tylko przy zakładaniu. */
  availableCurrencies: Currency[];
  onClose: () => void;
}

const SetTourBudgetModal = ({
  tourId,
  budget,
  availableCurrencies,
  onClose,
}: SetTourBudgetModalProps) => {
  const setBudget = useSetTourBudget(tourId);
  const editing = budget !== null && isBudgetDeclared(budget);

  const methods = useForm<TourBudgetFormData>({
    resolver: zodResolver(tourBudgetSchema),
    defaultValues: {
      currency: budget?.currency ?? availableCurrencies[0] ?? Currency.PLN,
      // Zero, nie pustka: puste pole liczbowe wraca z formularza jako NaN.
      fixedAmount: budget?.fixedAmount ?? 0,
      perParticipantAmount: budget?.perParticipantAmount ?? 0,
    },
  });

  const {
    handleSubmit,
    register,
    control,
    setError,
    formState: { errors },
  } = methods;

  const onSubmit = (data: TourBudgetFormData) =>
    setBudget.mutate(data, {
      onSuccess: () => onClose(),
      onError: (error) => {
        const field = getNegativeAmountField(error);
        if (field) setError(field, { message: 'Kwota nie może być ujemna' });
      },
    });

  return (
    <Modal onClose={onClose} className="max-w-[520px]">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            title={editing ? 'Edytuj budżet' : 'Ustaw budżet'}
            description="Plan liczy się z aktualnej liczby uczestników, więc zmienia się sam."
            onClose={onClose}
          />
          <ModalContent>
            <TwoInputsRow ratio="2/1">
              <ValueInput
                label="Kwota podstawowa"
                register={register('fixedAmount', { valueAsNumber: true })}
                placeholder="0"
                required
                type="number"
                step={0.01}
                error={errors.fixedAmount?.message}
                hint="Część budżetu niezależna od liczby uczestników."
              />
              <SingleSelectInput
                fieldName="currency"
                control={control}
                options={(budget ? [budget.currency] : availableCurrencies).map(
                  (currency) => ({
                    value: currency,
                    label: currencyLabels[currency],
                  }),
                )}
                label="Waluta"
                required
                error={errors.currency?.message}
                isDisabled={budget !== null}
              />
            </TwoInputsRow>

            <ValueInput
              label="Kwota na uczestnika"
              register={register('perParticipantAmount', {
                valueAsNumber: true,
              })}
              placeholder="0"
              required
              type="number"
              step={0.01}
              error={errors.perParticipantAmount?.message}
              hint="Doliczana za każdego uczestnika, więc plan zmienia się wraz z ich liczbą."
            />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={setBudget.isPending}
            >
              Zapisz
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default SetTourBudgetModal;
